/**
 * Cargador de controladores
 * @module classes/navigation/ControllerLoader
 */

/**
 * Clase para gestionar la carga de controladores
 * @class ControllerLoader
 */
export class ControllerLoader {
    /**
     * Constructor del ControllerLoader
     */
    constructor() {
        /** @type {Map<string, Object>} */
        this.controllerCache = new Map();

        this.controllers = {
            login: () => import('../../controllers/Login.js'),
            trip: () => import('../../controllers/Trip.js'),
            explore: () => import('../../controllers/Explore.js'),
            //experience: () => import('../../controllers/Experience.js'),
            profile: () => import('../../controllers/Profile.js'),
            uploadField: () => import('../../controllers/UploadField.js'),
            uploadFile: () => import('../../controllers/UploadFile.js'),
            uploadForm: () => import('../../controllers/UploadForm.js'),
            uploadLink: () => import('../../controllers/UploadLink.js'),
            uploadPayment: () => import('../../controllers/UploadPayment.js'),
            uploadJobOffer: () => import('../../controllers/UploadJobOffer.js'),
            reader: () => import('../../controllers/Reader.js'),
            viewer: () => import('../../controllers/Viewer.js'),
            challenge: () => import('../../controllers/Challenge.js'),
        };
    }

    /**
     * Obtiene un controlador por nombre (con cache)
     * @async
     * @param {string} controllerName - Nombre del controlador
     * @returns {Promise<Object|null>} Módulo del controlador o null
     */
    async getController(controllerName) {
        if (!controllerName) {
            return null;
        }

        if (this.controllerCache.has(controllerName)) {
            return this.controllerCache.get(controllerName);
        }

        if (this.controllers[controllerName]) {
            try {
                const module = await this.controllers[controllerName]();
                this.controllerCache.set(controllerName, module);
                return module;
            } catch (error) {
                throw error;
            }
        }

        return null;
    }

    /**
     * Verifica si un controlador es restaurable
     * @async
     * @param {string} controllerName - Nombre del controlador
     * @returns {Promise<boolean>} true si es restaurable
     */
    async isRestorable(controllerName) {
        if (!controllerName) return true;

        const module = await this.getController(controllerName);
        if (module && typeof module.default === 'function') {
            return typeof module.default.restorable === 'undefined' ? true : module.default.restorable;
        }
        return true;
    }

    /**
     * Verifica si un controlador oculta la navegación principal
     * @async
     * @param {string} controllerName - Nombre del controlador
     * @returns {Promise<boolean>} true si oculta la navegación
     */
    async hasHideMainNav(controllerName) {
        if (!controllerName) return false;

        const module = await this.getController(controllerName);
        if (module && typeof module.default === 'function') {
            return typeof module.default.hideMainNav === 'undefined' ? false : module.default.hideMainNav;
        }
        return false;
    }

    /**
     * Preload de un controlador
     * @async
     * @param {string} controllerName - Nombre del controlador
     * @returns {Promise<Object|null>} Módulo del controlador
     */
    async preloadController(controllerName) {
        if (this.controllerCache.has(controllerName)) {
            return this.controllerCache.get(controllerName);
        }

        try {
            const module = await this.getController(controllerName);
            if (module) return module;
        } catch (error) {
            throw error;
        }
    }
}

